/*
 * Iter 31.306 (D2) — useContentBlock hook.
 *
 * Any hard-coded string across the app can be migrated to
 * admin-editable content by wrapping with this hook:
 *
 *   const tagline = useContentBlock(
 *     "home.tagline",
 *     "Default hard-coded fallback.",
 *   );
 *
 * If the block exists in Mongo → returns its body_md.
 * If it doesn't (404, network error, first render) → returns
 *   the caller-provided fallback so the UI never shows blanks.
 *
 * Batched: the FIRST call fires a single `/api/content/blocks`
 * request that hydrates ALL blocks into a module-scope cache.
 * Every subsequent call reads from cache — zero extra HTTP.
 * Cache TTL matches the backend Cache-Control (5 min browser).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_TTL_MS = 5 * 60 * 1000;

let _cache = null;              // { blocks: { key: {body_md, ...} }, fetched_at }
let _inflight = null;           // Promise dedupe — first call wins, others await

async function _load() {
    if (_cache && (Date.now() - _cache.fetched_at) < CACHE_TTL_MS) {
        return _cache.blocks;
    }
    if (_inflight) return _inflight;
    _inflight = (async () => {
        try {
            const { data } = await axios.get(`${API}/api/content/blocks`);
            _cache = { blocks: data?.blocks || {}, fetched_at: Date.now() };
            return _cache.blocks;
        } catch {
            _cache = { blocks: {}, fetched_at: Date.now() };
            return {};
        } finally {
            _inflight = null;
        }
    })();
    return _inflight;
}

// Bust the cache after an admin edit — the ContentBlocksPanel calls
// this on save so the operator sees their change reflected without
// waiting for TTL.
export function invalidateContentBlockCache() {
    _cache = null;
    _inflight = null;
}

export function useContentBlock(key, fallback = "") {
    const [value, setValue] = useState(() => {
        // Synchronous fast-path — cache is warm from a prior hook call
        // on this page, so return the fresh body immediately.
        if (_cache && _cache.blocks[key]) return _cache.blocks[key].body_md;
        return fallback;
    });

    useEffect(() => {
        let cancelled = false;
        _load().then((blocks) => {
            if (cancelled) return;
            const b = blocks[key];
            if (b && typeof b.body_md === "string") setValue(b.body_md);
        });
        return () => { cancelled = true; };
    }, [key]);

    return value;
}
